// utils/uploadLimits.js
// ─────────────────────────────────────────────────────────────────────────────
// Client-side upload size limits. These MIRROR the multer limits configured in
// vc_be/middleware/fileUpload.js — keep them in sync. Files larger than these are
// rejected by the server, so validating here gives the user instant feedback
// instead of a failed upload after a long wait.
// ─────────────────────────────────────────────────────────────────────────────

export const BYTES_PER_MB = 1024 * 1024;
export const BYTES_PER_GB = 1024 * BYTES_PER_MB;

// Batch class recordings — POST /videos/upload (videoFile middleware).
// Streamed to R2 in multipart chunks, so this is a guardrail, not a RAM limit.
export const VIDEO_MAX_BYTES = 2 * BYTES_PER_GB;

// Course / batch / video materials AND self-paced lessons —
// POST /materials/upload and createLesson (materialFile middleware).
// Streamed to R2 in the controllers, so 2 GB is memory-safe (matches videos).
export const MATERIAL_MAX_BYTES = 2 * BYTES_PER_GB;

// Generic single-file upload — POST /files/upload (singleFile middleware).
export const FILE_MAX_BYTES = 100 * BYTES_PER_MB;

/** Human-friendly limit label, e.g. "2 GB", "300 MB". */
export const formatMaxSize = (bytes) => {
  if (bytes >= BYTES_PER_GB) {
    const gb = bytes / BYTES_PER_GB;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${Math.round(bytes / BYTES_PER_MB)} MB`;
};

/**
 * Human-friendly size of an actual file, scaling the unit to the value:
 * "812 B", "342.5 KB", "12.4 MB", "1.20 GB". Returns "" for 0/undefined.
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < BYTES_PER_GB) return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
  return `${(bytes / BYTES_PER_GB).toFixed(2)} GB`;
};

/**
 * Validate a File against a byte limit.
 * @returns {string|null} An error message if too large, otherwise null.
 */
export const validateFileSize = (file, maxBytes) => {
  if (file && file.size > maxBytes) {
    return `This file is ${formatFileSize(file.size)}, which exceeds the ${formatMaxSize(
      maxBytes
    )} limit. Please choose a smaller file.`;
  }
  return null;
};
